import { useState, useCallback, useRef } from 'react';
import {
  type SSEStreamState,
  SSEStreamStatus,
  type SSEChunkData,
  type SSECompleteData,
  type SSEErrorData,
} from '@/types/sse.types';
import { streamExampleText } from '@/api/streamingApi';

/**
 * Custom hook for managing SSE streaming state
 *
 * @returns Stream state and control functions
 *
 * @example
 * const { state, startStream, stopStream, resetStream } = useSSEStream();
 *
 * <button onClick={startStream}>Start</button>
 * <div>{state.content}</div>
 */
export function useSSEStream() {
  const [state, setState] = useState<SSEStreamState>({
    status: SSEStreamStatus.IDLE,
    content: '',
    chunks: [],
    metadata: null,
    error: null,
    isConnected: false,
    ttft: null,
  });

  const abortControllerRef = useRef<{ abort: () => void } | null>(null);
  const streamStartTimeRef = useRef<number>(0);

  /**
   * Start the SSE stream
   */
  const startStream = useCallback(() => {
    // Reset state first
    setState({
      status: SSEStreamStatus.CONNECTING,
      content: '',
      chunks: [],
      metadata: null,
      error: null,
      isConnected: false,
      ttft: null,
    });

    // Capture start time AFTER resetting state
    const startTime = Date.now();
    streamStartTimeRef.current = startTime;

    // Create SSE connection
    const controller = streamExampleText({
      onConnected: (data) => {
        setState((prev) => ({
          ...prev,
          status: SSEStreamStatus.STREAMING,
          isConnected: true,
        }));
      },

      onChunk: (data: SSEChunkData) => {
        setState((prev) => {
          // Calculate TTFT only for the FIRST chunk
          const isFirstChunk = prev.chunks.length === 0;
          const calculatedTtft = isFirstChunk
            ? Date.now() - streamStartTimeRef.current
            : prev.ttft;

          return {
            ...prev,
            content: prev.content + data.text,
            chunks: [...prev.chunks, data],
            ttft: calculatedTtft,
          };
        });
      },

      onComplete: (data: SSECompleteData) => {
        setState((prev) => ({
          ...prev,
          status: SSEStreamStatus.COMPLETED,
          metadata: data,
          isConnected: false,
        }));
      },

      onError: (data: SSEErrorData) => {
        setState((prev) => ({
          ...prev,
          status: SSEStreamStatus.ERROR,
          error: data.message || 'An error occurred',
          isConnected: false,
        }));
      },

      onAbort: () => {
        setState((prev) => ({
          ...prev,
          status: SSEStreamStatus.ABORTED,
          isConnected: false,
        }));
      },
    });

    abortControllerRef.current = controller;
  }, []);

  /**
   * Stop the SSE stream
   */
  const stopStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Reset the stream state
   */
  const resetStream = useCallback(() => {
    stopStream();
    streamStartTimeRef.current = 0;
    setState({
      status: SSEStreamStatus.IDLE,
      content: '',
      chunks: [],
      metadata: null,
      error: null,
      isConnected: false,
      ttft: null,
    });
  }, [stopStream]);

  /**
   * Check if stream is currently active
   */
  const isStreaming = state.status === SSEStreamStatus.STREAMING;

  /**
   * Check if stream can be started
   */
  const canStart =
    state.status === SSEStreamStatus.IDLE ||
    state.status === SSEStreamStatus.COMPLETED ||
    state.status === SSEStreamStatus.ERROR ||
    state.status === SSEStreamStatus.ABORTED;

  return {
    state,
    startStream,
    stopStream,
    resetStream,
    isStreaming,
    canStart,
  };
}
